import { createSlice, 
         PayloadAction 
       }                                              from "@reduxjs/toolkit";
import { Node, Edge, MarkerType }                                 from '@xyflow/react';
import { ServiceNodeData }                            from "features/workflow/CustomNode";
import { RootState }                                  from "store/Store";
import { APIData }                                    from "types/cms/frontendTypes";
import { Draft }                                      from "immer";

export interface WorkflowReducerState {
  workflows             : APIData<"api::feature-workflow.feature-workflow">[],
  systemNodes           : APIData<"api::system-node.system-node">[], 
  systemNodePositions   : APIData<"api::system-node-position.system-node-position">[],
  // Workflow specific state
  selectedWorkflowDocId : string,
  nodes                 : Node<ServiceNodeData>[],
  edges                 : Edge[],
}

const defaultState : WorkflowReducerState = {
  workflows             : [],
  systemNodes           : [],
  systemNodePositions   : [],
  selectedWorkflowDocId : '',
  nodes                 : [],
  edges                 : []
}

export const workflowSlice = createSlice({
  name: 'workflow',
  initialState: defaultState,
  reducers : {

    setWorkflows : (state : Draft<WorkflowReducerState>, 
                    action : PayloadAction<APIData<"api::feature-workflow.feature-workflow">[]>) => {
      state.workflows = action.payload;
    },

    selectWorkflow : (state : Draft<WorkflowReducerState>, 
                      action : PayloadAction<string>) => {

      if (action.payload === state.selectedWorkflowDocId) {
        return
      }

      state.selectedWorkflowDocId = action.payload;

      const selectedWorkflow = state.workflows.find(workflow => workflow.documentId === state.selectedWorkflowDocId)

      // Edges from Workflow
      const systemEdges = selectedWorkflow!.systemNodeEdges as unknown as APIData<"api::system-node-edge.system-node-edge">[]
      const edges = systemEdges.map(systemNodeEdge => convertSystemNodeEdgeToEdge(systemNodeEdge))

      // Nodes for Workflow
      const nodes = getNodesForWorkflow(state.selectedWorkflowDocId, state.systemNodePositions, state.systemNodes)

      state.edges = edges as any;
      state.nodes = nodes as any;
    },

    setSystemNodes : (state : Draft<WorkflowReducerState>, 
                      action : PayloadAction<APIData<"api::system-node.system-node">[]>) => {
      state.systemNodes = action.payload;
    },

    addSystemNodeAndPosition : (state : Draft<WorkflowReducerState>, 
                                action : PayloadAction<{
                                  systemNode : APIData<"api::system-node.system-node">,
                                  nodePosition : APIData<"api::system-node-position.system-node-position">
                                }>) => {

      state.systemNodes.push(action.payload.systemNode);
      state.systemNodePositions.push(action.payload.nodePosition);

      const nodes = getNodesForWorkflow(state.selectedWorkflowDocId, state.systemNodePositions, state.systemNodes)
      state.nodes = nodes as any;
    },

    updateSystemNode : (state : Draft<WorkflowReducerState>, 
                        action : PayloadAction<APIData<"api::system-node.system-node">>) => {
                          
      const index = state.systemNodes.findIndex(systemNode => systemNode.documentId === action.payload.documentId);
      state.systemNodes[index] = action.payload;

      const nodes = getNodesForWorkflow(state.selectedWorkflowDocId, state.systemNodePositions, state.systemNodes)
      state.nodes = nodes as any;
    },

    removeSystemNode : (state : Draft<WorkflowReducerState>, 
                        action : PayloadAction<string>) => {

      const index = state.systemNodes.findIndex(systemNode => systemNode.documentId === action.payload);
      state.systemNodes.splice(index, 1);

      const nodePositionIndex = state.systemNodePositions.findIndex(nodePosition => 
        (nodePosition.systemNode as any).documentId === action.payload)
      state.systemNodePositions.splice(nodePositionIndex, 1);

      const nodes = getNodesForWorkflow(state.selectedWorkflowDocId, state.systemNodePositions, state.systemNodes)
      state.nodes = nodes as any;
    },

    setNodePositions : (state : Draft<WorkflowReducerState>, 
                        action : PayloadAction<APIData<"api::system-node-position.system-node-position">[]>) => {
      state.systemNodePositions = action.payload;
    },

    addNodePosition : (state : Draft<WorkflowReducerState>, 
                        action : PayloadAction<APIData<"api::system-node-position.system-node-position">>) => {
      
      state.systemNodePositions.push(action.payload);

      const nodes = getNodesForWorkflow(state.selectedWorkflowDocId, state.systemNodePositions, state.systemNodes)
      state.nodes = nodes as any;
    },

    updateNodePosition : (state : Draft<WorkflowReducerState>, 
                          action : PayloadAction<APIData<"api::system-node-position.system-node-position">>) => {
      
      const index = state.systemNodePositions.findIndex(nodePosition => nodePosition.documentId === action.payload.documentId);
      state.systemNodePositions[index] = action.payload;

      const nodes = getNodesForWorkflow(state.selectedWorkflowDocId, state.systemNodePositions, state.systemNodes)
      state.nodes = nodes as any;
    },

    removeNodePosition : (state : Draft<WorkflowReducerState>, 
                          action : PayloadAction<string>) => {
      const index = state.systemNodePositions.findIndex(nodePosition => nodePosition.documentId === action.payload);
      state.systemNodePositions.splice(index, 1);

      const nodes = getNodesForWorkflow(state.selectedWorkflowDocId, state.systemNodePositions, state.systemNodes)
      state.nodes = nodes as any;
    },

    addEdge : (state : Draft<WorkflowReducerState>, 
              action : PayloadAction<APIData<"api::system-node-edge.system-node-edge">>) => {

      const updatedEdge = convertSystemNodeEdgeToEdge(action.payload)
      state.edges.push(updatedEdge as any);

      const updatedWorkflows = []
      for (const workflow of state.workflows) {
        if (workflow.documentId === state.selectedWorkflowDocId) {
          (workflow.systemNodeEdges as any).push(action.payload)
        }
        updatedWorkflows.push(workflow)
      }
      state.workflows = updatedWorkflows;
    },

    removeEdge : (state : Draft<WorkflowReducerState>, 
                 action : PayloadAction<string>) => {

      const index = state.edges.findIndex(edge => edge.id === action.payload);
      state.edges.splice(index, 1);

      const updatedWorkflows = []
      for (const workflow of state.workflows) {
        if (workflow.documentId === state.selectedWorkflowDocId) {
          const edges = (workflow.systemNodeEdges as any).filter(
              (edge : any) => edge.documentId !== action.payload)
          workflow.systemNodeEdges = edges
        }
        updatedWorkflows.push(workflow)
      }
      state.workflows = updatedWorkflows;
    },

    onDirectNodesChange : (state : Draft<WorkflowReducerState>, 
                          action : PayloadAction<Node<ServiceNodeData>[]>) => {
      state.nodes = action.payload as any;
    },

    onDirectEdgesChange : (state : Draft<WorkflowReducerState>, 
                          action : PayloadAction<Edge[]>) => {
      state.edges = action.payload as any;
    },

    handleWorkflowsOnUtilityChange : (state : Draft<WorkflowReducerState>) => {
    }

  }
});


export const {
  setWorkflows,
  selectWorkflow,
  setSystemNodes,
  addSystemNodeAndPosition,
  updateSystemNode,
  removeSystemNode,
  setNodePositions,
  addNodePosition,
  updateNodePosition,
  removeNodePosition,
  addEdge,
  removeEdge,
  onDirectNodesChange,
  onDirectEdgesChange,
  handleWorkflowsOnUtilityChange
} = workflowSlice.actions;

const getNodesForWorkflow = (workflowDocId : string, 
                             nodePositions : APIData<"api::system-node-position.system-node-position">[],
                             systemNodes : APIData<"api::system-node.system-node">[]) => {

  // Node Positions for Workflow
  const sysNodePositions = nodePositions.filter(nodePosition => 
      (nodePosition.featureWorkflow as any).documentId === workflowDocId)

  // Nodes
  const nodes = []
  for (const sysNodePosition of sysNodePositions) {
    const systemNode = systemNodes.find(systemNode => 
        systemNode.documentId === (sysNodePosition.systemNode as any).documentId)
    const node = convertSystemNodeAndPositionToNode(systemNode!, sysNodePosition)
    nodes.push(node)
  }
  return nodes
}

const convertSystemNodeAndPositionToNode = (systemNode : APIData<"api::system-node.system-node">, 
                                            nodePosition : APIData<"api::system-node-position.system-node-position">) 
    : Node<ServiceNodeData> => {

  return {
    id : systemNode.documentId,
    type : 'custom',
    position : {
      x : nodePosition.positionX as number,
      y : nodePosition.positionY as number
    },
    data : {
      label : systemNode.label as string,
      description : systemNode.description,
      type : systemNode.type as any,
      specifications : systemNode.specifications,
      configs : systemNode.configs as any,
      positionDocId : nodePosition.documentId
    }
  }
}

const convertSystemNodeEdgeToEdge = (systemNodeEdge: APIData<"api::system-node-edge.system-node-edge">) : Edge => {

  return {
    id : systemNodeEdge.documentId,
    source : systemNodeEdge.source as string,
    target : systemNodeEdge.target as string,
    label : systemNodeEdge.label,
    type : systemNodeEdge.type,
    sourceHandle : systemNodeEdge.sourceHandle,
    targetHandle : systemNodeEdge.targetHandle,
    markerEnd : {
      type : MarkerType.ArrowClosed,
      width : 40,
      height : 40
    }
  }
}


export const selectNodes = (state : RootState) => state.workflow.nodes;
export const selectEdges = (state : RootState) => state.workflow.edges;
export const selectWorkflows = (state : RootState) => state.workflow.workflows;
export const selectSystemNodes = (state : RootState) => state.workflow.systemNodes;
export const selectSystemNodePositions = (state : RootState) => state.workflow.systemNodePositions;
export const selectSelectedWorkflowDocId = (state : RootState) => state.workflow.selectedWorkflowDocId;
